// Configuration section panels for the 2D SVG Diagram tool.
//
// These are the "settings library" sections (Canvas, Reference image, Symmetry,
// Stroke styles, Connection presets, Effects, Pattern fills, Group outline
// presets) that used to live inline in `SvgDiagram2DControls`. They were lifted
// out so the canvas-side config tool rail (in `SvgDiagram2DViewer`) can render
// each one inside a popover while the control panel keeps the element lists and
// selection actions. `OutlineFieldsEditor` is exported because the panel's
// `GroupRow` still uses it.
import { useRef } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import {
    CONNECTION_OBJECT_ORDER_LABELS,
    CONNECTION_OBJECT_ORDERS,
    CONNECTION_OBJECT_ROTATION_LABELS,
    CONNECTION_OBJECT_ROTATIONS,
    CONNECTION_RENDER_KIND_LABELS,
    CONNECTION_RENDER_KINDS,
    CONNECTION_ROUTINGS,
    CORNER_STYLES,
    EFFECT_LABELS,
    newId,
    OUTLINE_SHAPES,
    PATTERN_ORDER_LABELS,
    PATTERN_ORDERS,
} from "../../utils/svgDiagram2DModel";
import type {
    ConnectionObject,
    ConnectionObjectOrder,
    ConnectionObjectRotation,
    ConnectionPreset,
    ConnectionRenderKind,
    ConnectionRouting,
    CornerStyle,
    DiagramPattern,
    Effect,
    GroupOutlinePreset,
    OutlineShape,
    OutlineStyleFields,
    PatternObject,
    PatternOrder,
    StrokeStyle,
    SymmetryAxis,
} from "../../utils/svgDiagram2DModel";
import { OBJECT_SVG_PRESETS } from "../../utils/pathPopulatePresets";
import { isValidSvg } from "../../utils/svgUtils";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { FillControl } from "../reusable/FillControl";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

const ROUTING_LABELS: Record<ConnectionRouting, string> = {
    straight: "Straight",
    orthogonal: "90° bends",
    diagonal: "45° bends",
    arc: "Arc",
    sine: "Sine wave",
};

const OUTLINE_SHAPE_LABELS: Record<OutlineShape, string> = {
    hull: "Hull (tight)",
    angular: "Angular (bends)",
    ortho: "Hull, 90° edges",
    diag: "Hull, 45° edges",
};

const CORNER_STYLE_LABELS: Record<CornerStyle, string> = {
    round: "Round",
    bevel: "Bevel",
};

const SYMMETRY_AXES: SymmetryAxis[] = ["x", "y"];

const rowStyle: React.CSSProperties = {
    border: "1px solid var(--control-border, #3a3a4a)",
    borderRadius: 6,
    padding: 8,
    marginBottom: 8,
    listStyle: "none",
};

// --- Connection object population editor ---------------------------------

/**
 * Editor for a preset's "objects along path" settings: pick built-in object
 * SVG presets or upload custom files, manage the chosen list, and set spacing /
 * size / rotation. Shown only when the preset's render kind populates objects.
 */
function ConnectionObjectsEditor({ style }: { style: StrokeStyle }) {
    const { updateStrokeStyle } = useSvgDiagram2D();
    const objects = style.objectSvgs ?? [];

    const addObject = (obj: ConnectionObject) => {
        updateStrokeStyle(style.id, { objectSvgs: [...objects, obj] });
    };
    const removeObject = (id: string) => {
        updateStrokeStyle(style.id, {
            objectSvgs: objects.filter((o) => o.id !== id),
        });
    };

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addObject({ id: newId("connobj"), name: file.name, svg: text });
            };
            reader.readAsText(file);
        }
    };

    const openUploadDialog = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = ".svg,image/svg+xml";
        input.style.display = "none";
        input.addEventListener("change", (e) => {
            importObjectFiles((e.target as HTMLInputElement).files ?? []);
            document.body.removeChild(input);
        });
        document.body.appendChild(input);
        input.click();
    };

    return (
        <div
            style={{
                marginTop: 6,
                paddingTop: 6,
                borderTop: "1px dashed var(--control-border, #3a3a4a)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
            }}
        >
            <p className="path-populate-preset-hint">Quick add preset</p>
            <div className="path-populate-preset-chips">
                {OBJECT_SVG_PRESETS.map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                        title={`Add ${p.label} as object`}
                        aria-label={`Add ${p.label} as object`}
                        onClick={() => {
                            if (!isValidSvg(p.svg)) return;
                            addObject({
                                id: newId("connobj"),
                                name: `${p.label} (preset)`,
                                svg: p.svg,
                            });
                        }}
                    >
                        <span
                            className="path-populate-preset-chip-icon"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: p.svg }}
                        />
                    </button>
                ))}
            </div>

            <div className="path-populate-object-rows">
                {objects.map((o) => (
                    <div key={o.id} className="path-populate-object-row">
                        <span
                            className="path-populate-preset-chip-icon"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: o.svg }}
                        />
                        <span
                            className="path-populate-object-name"
                            style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={o.name}
                        >
                            {o.name}
                        </span>
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--danger"
                            onClick={() => removeObject(o.id)}
                        >
                            <Trash2
                                className="tool-action-btn__icon"
                                size={16}
                                aria-hidden
                            />
                            <span>Remove</span>
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="tool-action-btn tool-action-btn--add"
                    onClick={openUploadDialog}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        importObjectFiles(e.dataTransfer.files);
                    }}
                >
                    <Plus
                        className="tool-action-btn__icon"
                        size={18}
                        strokeWidth={2.25}
                        aria-hidden
                    />
                    <span>Add object SVG</span>
                </button>
            </div>

            {objects.length === 0 && (
                <p className="path-populate-preset-hint" style={{ margin: 0 }}>
                    No objects yet — pick a preset above or upload an SVG. The
                    line is stroked until at least one object is added.
                </p>
            )}

            <div className="field-row field-row--two">
                <label className="field-label">
                    Spacing
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={style.objectSpacing}
                        title="Arc-length distance between adjacent objects along the path, in SVG units."
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                objectSpacing: Math.max(1, num(v, 24)),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Size
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={style.objectScale}
                        title="On-canvas size of each object's larger dimension, in SVG units."
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                objectScale: Math.max(1, num(v, 16)),
                            })
                        }
                    />
                </label>
            </div>

            <div className="field-row field-row--two">
                <label
                    className="field-label"
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                    }}
                    title="Override the fill of every object with one color. Off keeps each object's own colors."
                >
                    <input
                        type="checkbox"
                        checked={typeof style.objectColor === "string"}
                        onChange={(e) =>
                            updateStrokeStyle(style.id, {
                                objectColor: e.target.checked
                                    ? (style.objectColor ?? style.color)
                                    : undefined,
                            })
                        }
                    />
                    Recolor objects
                </label>
                {typeof style.objectColor === "string" && (
                    <label className="field-label">
                        Object color
                        <input
                            type="color"
                            value={style.objectColor}
                            onChange={(e) =>
                                updateStrokeStyle(style.id, {
                                    objectColor: e.target.value,
                                })
                            }
                        />
                    </label>
                )}
            </div>

            <label
                className="field-label"
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 6,
                }}
                title="Shift each object's color toward a heat zone's color by how close it is to that zone. Uses the object color (or the object's own colors) as the base."
            >
                <input
                    type="checkbox"
                    checked={style.heatReactive ?? false}
                    onChange={(e) =>
                        updateStrokeStyle(style.id, {
                            heatReactive: e.target.checked,
                        })
                    }
                />
                React to heat zones
            </label>

            {objects.length > 1 && (
                <label className="field-label">
                    Order
                    <select
                        value={style.objectOrder ?? "sequence"}
                        title="How objects are picked along the path: cycled in order, or a stable random shuffle."
                        onChange={(e) =>
                            updateStrokeStyle(style.id, {
                                objectOrder: e.target
                                    .value as ConnectionObjectOrder,
                            })
                        }
                    >
                        {CONNECTION_OBJECT_ORDERS.map((o) => (
                            <option key={o} value={o}>
                                {CONNECTION_OBJECT_ORDER_LABELS[o]}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            <div className="field-row field-row--two">
                <label className="field-label">
                    Rotation
                    <select
                        value={style.objectRotation}
                        onChange={(e) =>
                            updateStrokeStyle(style.id, {
                                objectRotation: e.target
                                    .value as ConnectionObjectRotation,
                            })
                        }
                    >
                        {CONNECTION_OBJECT_ROTATIONS.map((r) => (
                            <option key={r} value={r}>
                                {CONNECTION_OBJECT_ROTATION_LABELS[r]}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="field-label">
                    {style.objectRotation === "align"
                        ? "Angle offset (°)"
                        : "Angle (°)"}
                    <DebouncedInput
                        type="number"
                        step={1}
                        value={style.objectAngleDeg}
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                objectAngleDeg: num(v, 0),
                            })
                        }
                    />
                </label>
            </div>
        </div>
    );
}

// --- Pattern fill library ------------------------------------------------

/** One row in the pattern library: objects manager, name, tiling params, delete. */
function PatternRow({ pattern }: { pattern: DiagramPattern }) {
    const { updatePattern, removePattern } = useSvgDiagram2D();
    const objects = pattern.objects ?? [];

    const setObjects = (next: PatternObject[]) =>
        updatePattern(pattern.id, { objects: next });
    const addObject = (name: string, svg: string) =>
        setObjects([...objects, { id: newId("patobj"), name, svg }]);
    const removeObject = (id: string) =>
        setObjects(objects.filter((o) => o.id !== id));

    const importObjectFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addObject(file.name.replace(/\.svg$/i, ""), text);
            };
            reader.readAsText(file);
        }
    };

    const openObjectUploadDialog = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = ".svg,image/svg+xml";
        input.style.display = "none";
        input.addEventListener("change", (e) => {
            importObjectFiles((e.target as HTMLInputElement).files ?? []);
            document.body.removeChild(input);
        });
        document.body.appendChild(input);
        input.click();
    };

    return (
        <li style={rowStyle}>
            <div className="field-row" style={{ gap: 6, alignItems: "center" }}>
                <span
                    className="path-populate-preset-chip-icon"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: objects[0]?.svg ?? "" }}
                />
                <input
                    type="text"
                    value={pattern.name}
                    onChange={(e) =>
                        updatePattern(pattern.id, { name: e.target.value })
                    }
                    aria-label="Pattern name"
                    style={{ flex: 1, minWidth: 0 }}
                />
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removePattern(pattern.id)}
                    title="Remove pattern (clears fills using it)"
                >
                    <Trash2 size={14} aria-hidden />
                </button>
            </div>

            {/* --- Objects (tiles alternate between them) --- */}
            <p className="path-populate-preset-hint" style={{ marginTop: 6 }}>
                Objects {objects.length > 1 ? "(tiles alternate)" : ""}
            </p>
            <div
                className="path-populate-preset-chips"
                onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    importObjectFiles(e.dataTransfer.files);
                }}
            >
                {objects.map((o) => (
                    <span
                        key={o.id}
                        className="path-populate-preset-chip path-populate-preset-chip--icon"
                        title={o.name}
                        style={{ position: "relative" }}
                    >
                        <span
                            className="path-populate-preset-chip-icon"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: o.svg }}
                        />
                        {objects.length > 1 && (
                            <button
                                type="button"
                                className="tool-action-btn tool-action-btn--danger"
                                title={`Remove ${o.name}`}
                                aria-label={`Remove ${o.name}`}
                                onClick={() => removeObject(o.id)}
                                style={{
                                    position: "absolute",
                                    top: -6,
                                    right: -6,
                                    padding: 1,
                                    lineHeight: 0,
                                    borderRadius: "50%",
                                }}
                            >
                                <X size={10} aria-hidden />
                            </button>
                        )}
                    </span>
                ))}
            </div>

            <p className="path-populate-preset-hint">Add object</p>
            <div className="path-populate-preset-chips">
                {OBJECT_SVG_PRESETS.map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                        title={`Add ${p.label}`}
                        aria-label={`Add ${p.label}`}
                        onClick={() => {
                            if (!isValidSvg(p.svg)) return;
                            addObject(`${p.label} (preset)`, p.svg);
                        }}
                    >
                        <span
                            className="path-populate-preset-chip-icon"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: p.svg }}
                        />
                    </button>
                ))}
                <button
                    type="button"
                    className="generic-button"
                    onClick={openObjectUploadDialog}
                    title="Upload object SVG(s)"
                    aria-label="Upload object SVG"
                    style={{ padding: "2px 6px" }}
                >
                    <Plus size={14} aria-hidden />
                </button>
            </div>

            {objects.length > 1 && (
                <label className="field-label">
                    Order
                    <select
                        value={pattern.order ?? "sequence"}
                        title="How tiles pick objects across the repeating block: cycled in order, or a stable shuffle."
                        onChange={(e) =>
                            updatePattern(pattern.id, {
                                order: e.target.value as PatternOrder,
                            })
                        }
                    >
                        {PATTERN_ORDERS.map((o) => (
                            <option key={o} value={o}>
                                {PATTERN_ORDER_LABELS[o]}
                            </option>
                        ))}
                    </select>
                </label>
            )}

            <div className="field-row field-row--two">
                <label className="field-label">
                    Tile size
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={pattern.tileSize}
                        title="On-canvas size of each tile's larger dimension, in SVG units."
                        onChange={(v) =>
                            updatePattern(pattern.id, {
                                tileSize: Math.max(1, num(v, 24)),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Rotation (°)
                    <DebouncedInput
                        type="number"
                        step={1}
                        value={pattern.rotationDeg}
                        onChange={(v) =>
                            updatePattern(pattern.id, {
                                rotationDeg: num(v, 0),
                            })
                        }
                    />
                </label>
            </div>

            <div className="field-row field-row--two">
                <label className="field-label">
                    Spacing X
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={pattern.spacingX}
                        title="Horizontal gap between adjacent tiles, in SVG units."
                        onChange={(v) =>
                            updatePattern(pattern.id, {
                                spacingX: Math.max(0, num(v, 0)),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Spacing Y
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={pattern.spacingY}
                        title="Vertical gap between adjacent tiles, in SVG units."
                        onChange={(v) =>
                            updatePattern(pattern.id, {
                                spacingY: Math.max(0, num(v, 0)),
                            })
                        }
                    />
                </label>
            </div>

            <div
                className="field-row"
                style={{ gap: 8, alignItems: "center" }}
            >
                <CheckboxField
                    checked={pattern.backgroundColor != null}
                    onChange={(checked) =>
                        updatePattern(pattern.id, {
                            backgroundColor: checked ? "#ffffff" : null,
                        })
                    }
                    label="Tile background"
                    inline
                />
                {pattern.backgroundColor != null && (
                    <input
                        type="color"
                        aria-label="Pattern tile background color"
                        value={pattern.backgroundColor}
                        onChange={(e) =>
                            updatePattern(pattern.id, {
                                backgroundColor: e.target.value,
                            })
                        }
                    />
                )}
            </div>
        </li>
    );
}

/**
 * Pattern fill library: define reusable tiled-SVG patterns (quick-add a built-in
 * shape or upload an SVG), tune each one's tiling, then select it as the fill of
 * any rectangle, polygon, ellipse, or group outline.
 */
export function PatternLibrarySection() {
    const { doc, addPattern } = useSvgDiagram2D();

    const importPatternFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addPattern(file.name.replace(/\.svg$/i, ""), text);
            };
            reader.readAsText(file);
        }
    };

    const openUploadDialog = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = ".svg,image/svg+xml";
        input.style.display = "none";
        input.addEventListener("change", (e) => {
            importPatternFiles((e.target as HTMLInputElement).files ?? []);
            document.body.removeChild(input);
        });
        document.body.appendChild(input);
        input.click();
    };

    return (
        <>
            <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                Define a tiled-SVG pattern, then choose it as the fill of a rect,
                polygon, ellipse, or group outline.
            </p>

            <p className="path-populate-preset-hint">Quick add preset</p>
            <div className="path-populate-preset-chips">
                {OBJECT_SVG_PRESETS.map((p) => (
                    <button
                        key={p.id}
                        type="button"
                        className="generic-button path-populate-preset-chip path-populate-preset-chip--icon"
                        title={`Add ${p.label} as pattern`}
                        aria-label={`Add ${p.label} as pattern`}
                        onClick={() => {
                            if (!isValidSvg(p.svg)) return;
                            addPattern(`${p.label} (preset)`, p.svg);
                        }}
                    >
                        <span
                            className="path-populate-preset-chip-icon"
                            aria-hidden="true"
                            dangerouslySetInnerHTML={{ __html: p.svg }}
                        />
                    </button>
                ))}
            </div>

            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
                {doc.patterns.map((p) => (
                    <PatternRow key={p.id} pattern={p} />
                ))}
            </ul>

            <button
                type="button"
                className="tool-action-btn tool-action-btn--add"
                onClick={openUploadDialog}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    importPatternFiles(e.dataTransfer.files);
                }}
            >
                <Plus
                    className="tool-action-btn__icon"
                    size={18}
                    strokeWidth={2.25}
                    aria-hidden
                />
                <span>Upload pattern SVG</span>
            </button>
        </>
    );
}

// --- Stroke style row ----------------------------------------------------

/**
 * One row in the stroke-style library: the appearance of a stroked path
 * (color/width/dash, parallel lines, and objects-along-path), independent of
 * routing. Referenced by shapes/effects/outlines directly and by connection
 * presets via a picker.
 */
function StrokeStyleRow({
    style,
    total,
}: {
    style: StrokeStyle;
    total: number;
}) {
    const { updateStrokeStyle, removeStrokeStyle } = useSvgDiagram2D();
    return (
        <li style={rowStyle}>
            <div className="field-row" style={{ gap: 4, alignItems: "center" }}>
                <input
                    type="text"
                    value={style.name}
                    onChange={(e) =>
                        updateStrokeStyle(style.id, { name: e.target.value })
                    }
                    aria-label="Stroke style name"
                    style={{ flex: 1, minWidth: 0 }}
                />
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeStrokeStyle(style.id)}
                    disabled={total <= 1}
                    title="Remove stroke style (reverts anything using it)"
                    style={{ padding: "2px 6px" }}
                >
                    <Trash2 size={14} aria-hidden />
                </button>
            </div>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Color
                    <input
                        type="color"
                        value={style.color}
                        onChange={(e) =>
                            updateStrokeStyle(style.id, { color: e.target.value })
                        }
                    />
                </label>
                <label className="field-label">
                    Width
                    <DebouncedInput
                        type="number"
                        min={0.1}
                        step={0.1}
                        value={style.strokeWidth}
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                strokeWidth: Math.max(0.1, num(v, 2)),
                            })
                        }
                    />
                </label>
            </div>
            <label className="field-label">
                Dash (e.g. "4 2", empty = solid)
                <input
                    type="text"
                    value={style.dash}
                    onChange={(e) =>
                        updateStrokeStyle(style.id, { dash: e.target.value })
                    }
                    placeholder=""
                />
            </label>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Parallel lines
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={style.lineCount}
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                lineCount: Math.max(1, Math.round(num(v, 1))),
                            })
                        }
                    />
                </label>
                <label className="field-label">
                    Line spacing
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={0.5}
                        value={style.lineSpacing}
                        disabled={style.lineCount <= 1}
                        onChange={(v) =>
                            updateStrokeStyle(style.id, {
                                lineSpacing: Math.max(0, num(v, 6)),
                            })
                        }
                    />
                </label>
            </div>
            <label className="field-label">
                Render as
                <select
                    value={style.renderKind}
                    onChange={(e) =>
                        updateStrokeStyle(style.id, {
                            renderKind: e.target.value as ConnectionRenderKind,
                        })
                    }
                    title="Draw the path as a stroked line, as SVG objects populated along it, or both."
                >
                    {CONNECTION_RENDER_KINDS.map((k) => (
                        <option key={k} value={k}>
                            {CONNECTION_RENDER_KIND_LABELS[k]}
                        </option>
                    ))}
                </select>
            </label>
            {(style.renderKind === "objects" ||
                style.renderKind === "both") && (
                <ConnectionObjectsEditor style={style} />
            )}
        </li>
    );
}

// --- Preset row ----------------------------------------------------------

function PresetRow({
    preset,
    total,
    strokeStyles,
}: {
    preset: ConnectionPreset;
    total: number;
    strokeStyles: StrokeStyle[];
}) {
    const { updatePreset, removePreset } = useSvgDiagram2D();
    // The arc "keep spacing at endpoints" options only make sense with more than
    // one parallel line, which now lives on the referenced stroke style.
    const lineCount =
        strokeStyles.find((s) => s.id === preset.strokeStyleId)?.lineCount ?? 1;
    return (
        <li style={rowStyle}>
            <div className="field-row" style={{ gap: 4, alignItems: "center" }}>
                <input
                    type="text"
                    value={preset.name}
                    onChange={(e) => updatePreset(preset.id, { name: e.target.value })}
                    aria-label="Preset name"
                    style={{ flex: 1, minWidth: 0 }}
                />
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removePreset(preset.id)}
                    disabled={total <= 1}
                    title="Remove preset"
                    style={{ padding: "2px 6px" }}
                >
                    <Trash2 size={14} aria-hidden />
                </button>
            </div>
            <label className="field-label">
                Stroke style
                <select
                    value={preset.strokeStyleId}
                    disabled={strokeStyles.length === 0}
                    onChange={(e) =>
                        updatePreset(preset.id, {
                            strokeStyleId: e.target.value,
                        })
                    }
                    title="The stroke style that paints this connection's routed path."
                >
                    {strokeStyles.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </label>
            <label className="field-label">
                Routing
                <select
                    value={preset.routing}
                    onChange={(e) =>
                        updatePreset(preset.id, {
                            routing: e.target.value as ConnectionRouting,
                        })
                    }
                >
                    {CONNECTION_ROUTINGS.map((r) => (
                        <option key={r} value={r}>
                            {ROUTING_LABELS[r]}
                        </option>
                    ))}
                </select>
            </label>
            <CheckboxField
                checked={preset.bridge}
                onChange={(checked) =>
                    updatePreset(preset.id, { bridge: checked })
                }
                label="Bridge connected chains"
                title="When several connections using this preset meet at a shared node (one in, one out) with the same parallel-line count and routing, join them into one continuous, smooth stroke through that node instead of two lines ending at its center."
                inline
            />
            {(preset.routing === "orthogonal" ||
                preset.routing === "diagonal") && (
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Bends
                        <DebouncedInput
                            type="number"
                            min={1}
                            step={1}
                            value={preset.bendCount}
                            onChange={(v) =>
                                updatePreset(preset.id, {
                                    bendCount: Math.max(
                                        1,
                                        Math.round(num(v, 1)),
                                    ),
                                })
                            }
                        />
                    </label>
                    <label className="field-label">
                        Corner radius
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={preset.cornerRadius}
                            onChange={(v) =>
                                updatePreset(preset.id, {
                                    cornerRadius: Math.max(0, num(v, 0)),
                                })
                            }
                        />
                    </label>
                    {preset.cornerRadius > 0 && (
                        <label className="field-label">
                            Corner style
                            <select
                                value={preset.cornerStyle}
                                onChange={(e) =>
                                    updatePreset(preset.id, {
                                        cornerStyle: e.target
                                            .value as CornerStyle,
                                    })
                                }
                            >
                                {CORNER_STYLES.map((c) => (
                                    <option key={c} value={c}>
                                        {CORNER_STYLE_LABELS[c]}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>
            )}
            {preset.routing === "arc" && (
                <>
                    <label className="field-label">
                        Sweep (%)
                        <DebouncedInput
                            type="number"
                            min={1}
                            max={99}
                            step={1}
                            value={Math.round(preset.arcSweep * 100)}
                            title="Arc depth, as the chord (node-to-node distance) relative to the imaginary circle's diameter. 50% is a semicircle (chord = diameter); lower means a bigger circle and a flatter arc; higher bulges past a half-circle into a deeper loop."
                            onChange={(v) =>
                                updatePreset(preset.id, {
                                    arcSweep: Math.max(
                                        0.01,
                                        Math.min(
                                            0.99,
                                            num(v, preset.arcSweep * 100) / 100,
                                        ),
                                    ),
                                })
                            }
                        />
                    </label>
                    <CheckboxField
                        checked={preset.arcFlip}
                        onChange={(checked) =>
                            updatePreset(preset.id, { arcFlip: checked })
                        }
                        label="Flip arc side"
                        inline
                    />
                    <CheckboxField
                        checked={preset.arcSpacedEnds}
                        onChange={(checked) =>
                            updatePreset(preset.id, { arcSpacedEnds: checked })
                        }
                        label="Keep spacing at endpoints"
                        title="Parallel arcs stay a constant distance apart all the way to the endpoints instead of meeting at the same two points."
                        disabled={lineCount <= 1}
                        inline
                    />
                    <CheckboxField
                        checked={preset.arcEndsAlongChord}
                        onChange={(checked) =>
                            updatePreset(preset.id, {
                                arcEndsAlongChord: checked,
                            })
                        }
                        label="Stack endpoints along the line"
                        title="When keeping spacing at the endpoints, spread the endpoints along the connection line (arcs nest concentrically) instead of perpendicular to it. For two nodes on the same horizontal line the endpoints stack horizontally."
                        disabled={lineCount <= 1 || !preset.arcSpacedEnds}
                        inline
                    />
                </>
            )}
            {preset.routing === "sine" && (
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Amplitude
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={preset.sineAmplitude}
                            title="Peak displacement of the wave from the straight line, in SVG units. The wave swings this far to each side."
                            onChange={(v) =>
                                updatePreset(preset.id, {
                                    sineAmplitude: Math.max(0, num(v, 16)),
                                })
                            }
                        />
                    </label>
                    <label className="field-label">
                        Wavelength
                        <DebouncedInput
                            type="number"
                            min={1}
                            step={1}
                            value={preset.sineWavelength}
                            title="Length of one full wave cycle along the line, in SVG units. Smaller values pack more oscillations between the two nodes."
                            onChange={(v) =>
                                updatePreset(preset.id, {
                                    sineWavelength: Math.max(1, num(v, 48)),
                                })
                            }
                        />
                    </label>
                </div>
            )}
        </li>
    );
}

// --- Effect row (document-level, reusable) -------------------------------

function EffectRow({ effect }: { effect: Effect }) {
    const { doc, updateEffect, removeEffect } = useSvgDiagram2D();
    return (
        <li style={rowStyle}>
            <div className="field-row" style={{ gap: 4, alignItems: "center" }}>
                <input
                    type="text"
                    value={effect.name}
                    onChange={(e) => updateEffect(effect.id, { name: e.target.value })}
                    aria-label="Effect name"
                    style={{ flex: 1, minWidth: 0 }}
                />
                <span className="path-tool-meta" style={{ opacity: 0.6 }}>
                    {EFFECT_LABELS[effect.kind]}
                </span>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeEffect(effect.id)}
                    title="Remove effect"
                    style={{ padding: "2px 6px" }}
                >
                    <Trash2 size={14} aria-hidden />
                </button>
            </div>
            {effect.kind === "radial-emitter" && (
                <>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Rays
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={256}
                                step={1}
                                value={effect.rayCount}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        rayCount: Math.max(1, Math.round(num(v, 8))),
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Stroke style
                            <select
                                className="anchor-select"
                                value={effect.strokeStyleId ?? ""}
                                onChange={(e) =>
                                    updateEffect(effect.id, {
                                        strokeStyleId: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">Default</option>
                                {doc.strokeStyles.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Inner radius
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={effect.innerRadius}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        innerRadius: Math.max(0, num(v, 14)),
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Outer radius
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={effect.outerRadius}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        outerRadius: Math.max(0, num(v, 40)),
                                    })
                                }
                            />
                        </label>
                    </div>
                </>
            )}
            {effect.kind === "concentric-rings" && (
                <>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Rings
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={64}
                                step={1}
                                value={effect.ringCount}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        ringCount: Math.max(
                                            1,
                                            Math.round(num(v, 3)),
                                        ),
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Stroke style
                            <select
                                className="anchor-select"
                                value={effect.strokeStyleId ?? ""}
                                onChange={(e) =>
                                    updateEffect(effect.id, {
                                        strokeStyleId: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">Default</option>
                                {doc.strokeStyles.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Inner radius
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={effect.innerRadius}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        innerRadius: Math.max(0, num(v, 18)),
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Spacing
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={effect.spacing}
                                onChange={(v) =>
                                    updateEffect(effect.id, {
                                        spacing: Math.max(0, num(v, 12)),
                                    })
                                }
                            />
                        </label>
                    </div>
                </>
            )}
        </li>
    );
}

// --- Outline style fields editor -----------------------------------------

/**
 * The shared editor for a group outline's stylable fields (padding, stroke
 * style, shape, corners, fill, …). Used both by a reusable outline preset and
 * by a group's own standalone outline, so the two always look and behave the
 * same. The caller owns the values and applies `onChange` partials.
 */
export function OutlineFieldsEditor({
    fields,
    strokeStyles,
    patterns,
    onChange,
}: {
    fields: OutlineStyleFields;
    strokeStyles: StrokeStyle[];
    patterns: DiagramPattern[];
    onChange: (partial: Partial<OutlineStyleFields>) => void;
}) {
    return (
        <>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Padding
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={fields.padding}
                        onChange={(v) =>
                            onChange({ padding: Math.max(0, num(v, 16)) })
                        }
                    />
                </label>
                <label className="field-label">
                    Stroke style
                    <select
                        className="anchor-select"
                        value={fields.strokeStyleId ?? ""}
                        onChange={(e) =>
                            onChange({ strokeStyleId: e.target.value || null })
                        }
                    >
                        <option value="">Default</option>
                        {strokeStyles.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Shape
                    <select
                        className="anchor-select"
                        value={fields.shape}
                        onChange={(e) =>
                            onChange({ shape: e.target.value as OutlineShape })
                        }
                    >
                        {OUTLINE_SHAPES.map((s) => (
                            <option key={s} value={s}>
                                {OUTLINE_SHAPE_LABELS[s]}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="field-label">
                    Corner radius
                    <DebouncedInput
                        type="number"
                        min={0}
                        step={1}
                        value={fields.cornerRadius}
                        onChange={(v) =>
                            onChange({ cornerRadius: Math.max(0, num(v, 0)) })
                        }
                    />
                </label>
            </div>
            {fields.cornerRadius > 0 && (
                <label className="field-label">
                    Corner style
                    <select
                        value={fields.cornerStyle}
                        onChange={(e) =>
                            onChange({
                                cornerStyle: e.target.value as CornerStyle,
                            })
                        }
                    >
                        {CORNER_STYLES.map((c) => (
                            <option key={c} value={c}>
                                {CORNER_STYLE_LABELS[c]}
                            </option>
                        ))}
                    </select>
                </label>
            )}
            {fields.shape === "angular" && (
                <label className="field-label">
                    Bends (2 = box, 4 = octagon)
                    <DebouncedInput
                        type="number"
                        min={2}
                        step={1}
                        value={fields.bendCount}
                        onChange={(v) =>
                            onChange({
                                bendCount: Math.max(2, Math.round(num(v, 4))),
                            })
                        }
                    />
                </label>
            )}
            {(fields.shape === "ortho" || fields.shape === "diag") && (
                <label className="field-label">
                    Bends per edge
                    <DebouncedInput
                        type="number"
                        min={1}
                        step={1}
                        value={fields.bendCount}
                        onChange={(v) =>
                            onChange({
                                bendCount: Math.max(1, Math.round(num(v, 1))),
                            })
                        }
                    />
                </label>
            )}
            <div className="field-row" style={{ gap: 8, alignItems: "center" }}>
                <FillControl
                    fill={fields.fill}
                    patterns={patterns}
                    onChange={(fill) => onChange({ fill })}
                    label="Outline"
                />
            </div>
            {fields.shape === "hull" && (
                <CheckboxField
                    checked={fields.closed}
                    onChange={(checked) => onChange({ closed: checked })}
                    label="Closed (encircle)"
                    inline
                />
            )}
        </>
    );
}

// --- Group outline presets ------------------------------------------------

/** One reusable group-outline preset: rename, edit its fields, or delete. */
function GroupOutlinePresetRow({ preset }: { preset: GroupOutlinePreset }) {
    const { doc, updateGroupOutlinePreset, removeGroupOutlinePreset } =
        useSvgDiagram2D();
    const linkedCount = doc.groups.filter((g) =>
        g.properties.some(
            (p) => p.kind === "outline" && p.presetId === preset.id,
        ),
    ).length;
    return (
        <li style={rowStyle}>
            <div className="field-row" style={{ gap: 4, alignItems: "center" }}>
                <input
                    type="text"
                    value={preset.name}
                    onChange={(e) =>
                        updateGroupOutlinePreset(preset.id, {
                            name: e.target.value,
                        })
                    }
                    aria-label="Preset name"
                    style={{ flex: 1, minWidth: 0 }}
                />
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeGroupOutlinePreset(preset.id)}
                    title="Remove preset (unlinks its groups)"
                    style={{ padding: "2px 6px" }}
                >
                    <Trash2 size={14} aria-hidden />
                </button>
            </div>
            <p className="path-tool-meta" style={{ margin: "4px 0", opacity: 0.7 }}>
                Used by {linkedCount} group{linkedCount === 1 ? "" : "s"}
            </p>
            <OutlineFieldsEditor
                fields={preset}
                strokeStyles={doc.strokeStyles}
                patterns={doc.patterns}
                onChange={(partial) =>
                    updateGroupOutlinePreset(preset.id, partial)
                }
            />
        </li>
    );
}

/** The presets editor + an "apply to all groups" control. */
export function GroupOutlinePresetsEditor() {
    const {
        doc,
        addGroupOutlinePreset,
        applyOutlinePresetToAllGroups,
    } = useSvgDiagram2D();
    const presets = doc.groupOutlinePresets;
    return (
        <div>
            <p className="path-tool-meta" style={{ margin: "0 0 8px" }}>
                Reusable outline styles. Link a group's outline to a preset
                below; edit the preset once and every linked group updates.
            </p>
            {presets.length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {presets.map((p) => (
                        <GroupOutlinePresetRow key={p.id} preset={p} />
                    ))}
                </ul>
            )}
            <button
                type="button"
                className="generic-button"
                onClick={() => addGroupOutlinePreset()}
                style={{ marginTop: 6 }}
            >
                <Plus size={14} aria-hidden /> Outline preset
            </button>
            {presets.length > 0 && doc.groups.length > 0 && (
                <div
                    className="field-row"
                    style={{ gap: 6, alignItems: "flex-end", marginTop: 10 }}
                >
                    <label className="field-label" style={{ flex: 1 }}>
                        Apply preset to all groups
                        <select
                            className="anchor-select"
                            defaultValue=""
                            onChange={(e) => {
                                if (!e.target.value) return;
                                applyOutlinePresetToAllGroups(e.target.value);
                                e.target.value = "";
                            }}
                        >
                            <option value="">Choose preset…</option>
                            {presets.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            )}
        </div>
    );
}

// --- Section panels (rail popover bodies) --------------------------------
//
// Each export below is the body of one config section the canvas-side tool rail
// shows in a popover.

export function CanvasSection() {
    const {
        viewBoxWidth,
        setViewBoxWidth,
        viewBoxHeight,
        setViewBoxHeight,
        background,
        setBackground,
        snapEnabled,
        setSnapEnabled,
    } = useSvgDiagram2D();
    return (
        <>
            <div className="field-row field-row--two">
                <label className="field-label">
                    Width
                    <DebouncedInput
                        type="number"
                        min={50}
                        max={4000}
                        step={10}
                        value={viewBoxWidth}
                        onChange={(v) =>
                            setViewBoxWidth(Math.max(50, Math.round(num(v, 1414))))
                        }
                    />
                </label>
                <label className="field-label">
                    Height
                    <DebouncedInput
                        type="number"
                        min={50}
                        max={4000}
                        step={10}
                        value={viewBoxHeight}
                        onChange={(v) =>
                            setViewBoxHeight(Math.max(50, Math.round(num(v, 1000))))
                        }
                    />
                </label>
            </div>
            <label className="field-label">
                Background
                <input
                    type="color"
                    value={background}
                    onChange={(e) => setBackground(e.target.value)}
                />
            </label>
            <CheckboxField
                checked={snapEnabled}
                onChange={setSnapEnabled}
                label="Snap to nodes & canvas guides (M)"
                inline
            />
        </>
    );
}

export function ReferenceImageSection() {
    const {
        refImage,
        setRefImageFromFile,
        clearRefImage,
        refImageVisible,
        setRefImageVisible,
        refImageOpacity,
        setRefImageOpacity,
    } = useSvgDiagram2D();
    const refImageInputRef = useRef<HTMLInputElement>(null);
    return (
        <>
            <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                Show an image beneath the diagram to trace over. It's a
                canvas-only guide — it isn't saved or included in the
                exported SVG.
            </p>
            <div className="upload-controls">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => refImageInputRef.current?.click()}
                >
                    {refImage ? "Replace image..." : "Choose image file..."}
                </button>
                <input
                    ref={refImageInputRef}
                    type="file"
                    accept="image/*"
                    className="upload-input"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setRefImageFromFile(file);
                        // Allow re-picking the same file later.
                        e.target.value = "";
                    }}
                />
            </div>
            {refImage && (
                <>
                    <p className="upload-info">
                        Loaded: <strong>{refImage.name}</strong>
                    </p>
                    <CheckboxField
                        checked={refImageVisible}
                        onChange={setRefImageVisible}
                        label="Show on canvas"
                        inline
                    />
                    <label className="field-label">
                        Opacity ({Math.round(refImageOpacity * 100)}%)
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={refImageOpacity}
                            onChange={(e) =>
                                setRefImageOpacity(num(e.target.value, 0.5))
                            }
                        />
                    </label>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={clearRefImage}
                    >
                        Remove reference image
                    </button>
                </>
            )}
        </>
    );
}

export function SymmetrySection() {
    const {
        doc,
        setSymmetryAxis,
        setSymmetryGuide,
        forceSymmetry,
        selectedNodeIds,
        selectedEllipseIds,
        selectedLineIds,
        selectedArcIds,
        selectedRectIds,
        selectedPolygonIds,
    } = useSvgDiagram2D();
    const selectedCount =
        selectedNodeIds.length +
        selectedEllipseIds.length +
        selectedLineIds.length +
        selectedArcIds.length +
        selectedRectIds.length +
        selectedPolygonIds.length;
    const anyAxis = doc.symmetry.x || doc.symmetry.y;
    return (
        <>
            <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                With an axis active, placing, moving, duplicating, or
                connecting a node also acts on the matching node at its
                mirrored position across the canvas centerline. Nodes
                stay independent — there are no locked mirrors.
            </p>
            <div className="field-row" style={{ gap: 12 }}>
                {SYMMETRY_AXES.map((axis) => (
                    <CheckboxField
                        key={axis}
                        checked={doc.symmetry[axis]}
                        onChange={(checked) => setSymmetryAxis(axis, checked)}
                        label={`${axis.toUpperCase()} axis`}
                        inline
                    />
                ))}
            </div>
            <p className="path-tool-meta" style={{ margin: "10px 0 6px" }}>
                Center guides are editor-only reference lines through the
                canvas center. They don't export.
            </p>
            <div className="field-row" style={{ gap: 12 }}>
                <CheckboxField
                    checked={doc.symmetry.guideX}
                    onChange={(checked) => setSymmetryGuide("guideX", checked)}
                    label="X center guide"
                    inline
                />
                <CheckboxField
                    checked={doc.symmetry.guideY}
                    onChange={(checked) => setSymmetryGuide("guideY", checked)}
                    label="Y center guide"
                    inline
                />
            </div>
            {selectedCount >= 2 && (
                <>
                    <button
                        type="button"
                        className="generic-button"
                        style={{ marginTop: 8 }}
                        disabled={!anyAxis}
                        onClick={forceSymmetry}
                    >
                        Force symmetry on selection
                    </button>
                    <p className="path-tool-meta" style={{ margin: "6px 0 0" }}>
                        {anyAxis
                            ? "Snaps mirror-matched pairs in the selection to exact symmetry across the active axis. Unpaired elements stay put."
                            : "Activate an axis to enable forcing symmetry."}
                    </p>
                </>
            )}
        </>
    );
}

export function StrokeStylesSection() {
    const { doc, addStrokeStyle } = useSvgDiagram2D();
    return (
        <>
            <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                Reusable stroke looks (color, parallel lines, objects)
                referenced by shapes, effects, outlines, and connection
                presets.
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {doc.strokeStyles.map((s) => (
                    <StrokeStyleRow
                        key={s.id}
                        style={s}
                        total={doc.strokeStyles.length}
                    />
                ))}
            </ul>
            <button
                type="button"
                className="generic-button"
                onClick={addStrokeStyle}
            >
                <Plus size={16} aria-hidden /> Add stroke style
            </button>
        </>
    );
}

export function ConnectionPresetsSection() {
    const { doc, activePresetId, setActivePresetId, addPreset } =
        useSvgDiagram2D();
    return (
        <>
            <label className="field-label">
                Active preset (for Connect tool & new properties)
                <select
                    className="anchor-select"
                    value={activePresetId ?? ""}
                    onChange={(e) => setActivePresetId(e.target.value || null)}
                >
                    {doc.presets.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
            </label>
            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
                {doc.presets.map((p) => (
                    <PresetRow
                        key={p.id}
                        preset={p}
                        total={doc.presets.length}
                        strokeStyles={doc.strokeStyles}
                    />
                ))}
            </ul>
            <button type="button" className="generic-button" onClick={addPreset}>
                <Plus size={16} aria-hidden /> Add preset
            </button>
        </>
    );
}

export function EffectsSection() {
    const { doc, addEffect } = useSvgDiagram2D();
    return (
        <>
            <p className="path-tool-meta" style={{ margin: "0 0 6px" }}>
                Define a reusable effect, then toggle it on any node from
                the node list or its panel.
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {doc.effects.map((eff) => (
                    <EffectRow key={eff.id} effect={eff} />
                ))}
            </ul>
            <div className="field-row" style={{ gap: 4, flexWrap: "wrap" }}>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => addEffect("radial-emitter")}
                >
                    <Plus size={16} aria-hidden /> Add radial emitter
                </button>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => addEffect("concentric-rings")}
                >
                    <Plus size={16} aria-hidden /> Add concentric rings
                </button>
            </div>
        </>
    );
}

export function GroupOutlinePresetsSection() {
    return <GroupOutlinePresetsEditor />;
}
